// How old the numbers on screen are.
//
// The board is read on a phone, mid-fight, and every figure on it is a
// second-hand copy: the roster comes from a cache the worker refreshes on a
// TTL, and contract damage only moves while somebody in the unit has the game
// open. Both can sit still for a long time while looking perfectly current, so
// the page says outright how old what it shows is.

/** Below this, "now" is the honest word and a number would be noise. */
const JUST_NOW_MS = 45_000;

/** Coarse "N ago" words for an age in milliseconds. */
export function ageWords(ageMs: number): string {
  if (!Number.isFinite(ageMs) || ageMs < JUST_NOW_MS) return "just now";
  const mins = Math.floor(ageMs / 60_000);
  if (mins < 60) return Math.max(1, mins) + " min ago";
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours + (hours === 1 ? " hour ago" : " hours ago");
  const days = Math.floor(hours / 24);
  return days + (days === 1 ? " day ago" : " days ago");
}

/**
 * How much to make of that age.
 *
 * "fresh" is not worth a word on screen; "aging" is worth stating plainly;
 * "stale" means the reader should not act on the number without checking the
 * game itself. The thresholds differ per figure, so the caller passes them.
 */
export type AgeTone = "fresh" | "aging" | "stale";

export function ageTone(ageMs: number, agingMs: number, staleMs: number): AgeTone {
  if (ageMs >= staleMs) return "stale";
  if (ageMs >= agingMs) return "aging";
  return "fresh";
}

/** The roster is cached for a minute; past five it is worth saying so, and past
 * fifteen the health bars are guesswork. */
export const ROSTER_AGING_MS = 5 * 60_000;
export const ROSTER_STALE_MS = 15 * 60_000;

/** Contract damage only advances while a member has the game open. A couple of
 * minutes is ordinary; a quarter of an hour means nobody is relaying. */
export const DAMAGE_AGING_MS = 3 * 60_000;
export const DAMAGE_STALE_MS = 15 * 60_000;

/**
 * The age of what the board is showing, from the two clocks that matter.
 *
 * `polledAtMs` is when this page last heard from the service at all — a dead
 * network is a different failure from a service serving old figures, and the
 * reader has to be able to tell them apart.
 */
export interface DataAge {
  /** Since this page last completed a poll. */
  polledMs: number;
  /** Since the game was read for the roster (health, hits, who is online). */
  rosterMs: number;
  /** Since the contract's damage figures were last relayed, when known. */
  damageMs: number | null;
}

export function dataAge(
  nowMs: number,
  polledAtMs: number | null,
  rosterFetchedAtMs: number | null,
  damageUpdatedAtMs: number | null,
): DataAge {
  return {
    polledMs: polledAtMs === null ? 0 : Math.max(0, nowMs - polledAtMs),
    rosterMs: rosterFetchedAtMs === null ? 0 : Math.max(0, nowMs - rosterFetchedAtMs),
    damageMs: damageUpdatedAtMs === null ? null : Math.max(0, nowMs - damageUpdatedAtMs),
  };
}
